import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import adminApi from '../../services/adminApi';
import LoadingSpinner from '../../components/LoadingSpinner';
import { 
  IoPersonOutline, 
  IoPeopleOutline,
  IoBookOutline,
  IoTrophyOutline,
  IoStatsChartOutline,
  IoArrowForwardOutline
} from 'react-icons/io5';
import './AdminDashboardPage.css';

const AdminDashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user?.isAdmin) {
      loadDashboardStats();
    }
  }, [user]);

  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await adminApi.get('/admin/dashboard');
      setDashboardStats(response.data.dashboardStats);
    } catch (error) {
      console.error('Load dashboard stats error:', error);
      setError('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  if (!user?.isAdmin) {
    return (
      <div className="admin-access-denied">
        <h1>Access Denied</h1>
        <p>You don't have permission to access this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-loading">
        <LoadingSpinner size="large" message="Loading dashboard..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-error">
        <h2>Error Loading Dashboard</h2>
        <p>{error}</p>
        <button onClick={loadDashboardStats} className="retry-btn">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-page">
      <div className="admin-page-header">
        <h1 className="admin-page-title">Admin Dashboard</h1>
        <p className="admin-page-description">
          Overview of your ReadTheWord application
        </p>
      </div>

      {/* Quick Stats */}
      <div className="admin-stats-grid">
        <div className="admin-stat-card" onClick={() => navigate('/admin/users')}>
          <div className="stat-icon">
            <IoPersonOutline />
          </div>
          <div className="stat-content">
            <h3 className="admin-stat-value">{dashboardStats?.totalUsers || 0}</h3>
            <p className="admin-stat-label">Total Users</p>
          </div>
          <div className="stat-action">
            <IoArrowForwardOutline />
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="stat-icon">
            <IoPeopleOutline />
          </div>
          <div className="stat-content">
            <h3 className="admin-stat-value">{dashboardStats?.totalReaders || 0}</h3>
            <p className="admin-stat-label">Active Readers</p>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="stat-icon">
            <IoBookOutline />
          </div>
          <div className="stat-content">
            <h3 className="admin-stat-value">{dashboardStats?.totalReports || 0}</h3>
            <p className="admin-stat-label">Reading Reports</p>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="stat-icon">
            <IoTrophyOutline />
          </div>
          <div className="stat-content">
            <h3 className="admin-stat-value">{dashboardStats?.activeGoals || 0}</h3>
            <p className="admin-stat-label">Active Goals</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">Quick Actions</h2>
        </div>
        <div className="quick-actions-grid">
          <button 
            className="quick-action-btn"
            onClick={() => navigate('/admin/families')}
          >
            <IoPeopleOutline />
            <span>Manage Family Groups</span>
            <IoArrowForwardOutline />
          </button>
          
          <button 
            className="quick-action-btn"
            onClick={() => navigate('/admin/users')}
          >
            <IoPersonOutline />
            <span>Manage Users</span>
            <IoArrowForwardOutline />
          </button>
          
          <button 
            className="quick-action-btn"
            onClick={() => navigate('/admin/goals')}
          >
            <IoTrophyOutline />
            <span>Site Goals</span>
            <IoArrowForwardOutline />
          </button>
          
          <button 
            className="quick-action-btn"
            onClick={() => navigate('/admin/reports')}
          >
            <IoBookOutline />
            <span>Reading Reports</span>
            <IoArrowForwardOutline />
          </button>
        </div>
      </div>

      {/* Active Goals Overview */}
      {dashboardStats?.goals && dashboardStats.goals.length > 0 && (
        <div className="admin-card">
          <div className="admin-card-header">
            <h2 className="admin-card-title">Active Site Goals</h2>
          </div>
          <div className="goals-overview">
            {dashboardStats.goals.slice(0, 3).map((goal) => (
              <div key={goal.id} className="goal-item">
                <div className="goal-info">
                  <h4>{goal.name}</h4>
                  <p>{goal.description}</p>
                </div>
                <div className="goal-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${Math.min((goal.progress / goal.targetValue) * 100, 100)}%` }}
                    />
                  </div>
                  <span className="progress-text">
                    {goal.progress} / {goal.targetValue} {goal.unit}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* System Status */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">
            <IoStatsChartOutline />
            System Overview
          </h2>
        </div>
        <div className="system-status">
          <div className="status-item">
            <label>Total Goals:</label>
            <span>{dashboardStats?.totalGoals || 0}</span>
          </div>
          <div className="status-item">
            <label>Active Goals:</label>
            <span>{dashboardStats?.activeGoals || 0}</span>
          </div>
          <div className="status-item">
            <label>System Status:</label>
            <span className="status-healthy">Healthy</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
